'use client'

/**
 * Documentation section for /products/avery-prismapro: the four official
 * Avery Dennison PrismaPro PDFs Peter supplied
 * (scripts/supplier-assets/intake-prismapro-docs/), converted to HTML and
 * images rather than embedded as PDFs or offered as download-only links.
 * Follows the pattern established in avery-sw900.tsx.
 *
 * - Colour swatch poster: rendered to two webp sizes by
 *   scripts/supplier-assets/wire-product-docs.ts and uploaded to S3. Shown
 *   here as a click-to-expand image (URLs below are constants -- no DB
 *   Media row backs them).
 * - Product Overview (doc A557445, dated 12/2025): transcribed BY HAND from
 *   `pdftotext -layout ad-gs-print-product-overview-prismapro-color-ppf.pdf`.
 * - Product Data Sheet (Revision 0, dated 05/15/26): transcribed from
 *   `pdftotext -layout pds-prismapro-ppf.pdf`, in the PDF's own section order.
 * - Application Guidelines (Instructional Bulletin #4.40, Revision 0, dated
 *   05/15/26): transcribed from
 *   `pdftotext -layout ib440-dry-apply-ppf-guidelines.pdf`. Structural
 *   content (headings, steps, tool names, cautions, figures) is verbatim;
 *   the section 6.4 "video coming soon" placeholder line and running
 *   page-footer text ("Section 4 - Application and Removal Information...")
 *   are omitted as non-substantive.
 * - Warranty (dated 06/2026): transcribed from
 *   `pdftotext -layout grs-ad-ppf-warranty.pdf`.
 *
 * Every figure, unit, and claim is copied exactly from the source PDFs. The
 * only edits made to the extracted text are:
 * (a) one em-dash (an actual U+2014 "-", verified against the PDF) joining
 *     two independent clauses in the Product Overview intro, replaced with
 *     a semicolon per house style: "This is more than just a film-it's an
 *     evolution in vehicle customization." -> "This is more than just a
 *     film; it's an evolution in vehicle customization."
 * (b) en-dashes used as bullet markers throughout the Overview and IB440
 *     source PDFs are dropped entirely, since this component renders those
 *     items as semantic <ul>/<li> lists rather than literal dash
 *     characters. Where an en-dash was used inline as a label separator
 *     (e.g. "Tool belt - to hold all application tools", and every item in
 *     IB440's "Working With PrismaPro" DO/DON'T comparison), it was
 *     replaced with a colon, matching the house-style substitution table
 *     for "introducing an explanation".
 * (c) two missing-space PDF text-layer artifacts fixed by inserting a
 *     space, the same category of fix avery-sw900.tsx made: "PrismaPro(TM)
 *     Color PPF" (IB440 4.0, printed as one run with no space) and "0(deg)
 *     and 45(deg) angle" (IB440 9.0, printed as "0(deg)and 45(deg)angle").
 * (d) the three ALL-CAPS legal paragraphs in the warranty PDF's "Claims"
 *     section are rendered in sentence case with a Tailwind `uppercase`
 *     class applied for the same shouting visual effect, mirroring the
 *     identical convention avery-sw900.tsx already established for its own
 *     ALL-CAPS warranty paragraphs (confirmed against that PDF, which is
 *     also ALL CAPS in the source and rendered in sentence case there).
 * No figure, unit, or word was otherwise added, removed, or rounded.
 * Two redundant-fact omissions (per the "avoid duplicate rows" rule): the
 * warranty PDF's "PrismaPro(TM) PPF Durability" zone table (Zone 1/2/3
 * Vertical/Horizontal: 10/8/6 years, 8/6/5 years) is skipped because it
 * exactly duplicates the same figures already transcribed in the Product
 * Data Sheet's Physical Characteristics table; and the Product Data
 * Sheet's own generic "Warranty:" paragraph (identical Avery Dennison PDS
 * boilerplate reused across their product line) is skipped in favor of the
 * dedicated, more specific Warranty PDF.
 *
 * See scripts/supplier-assets/intake-prismapro-docs/ for source PDFs.
 */
import { Disclosure, DisclosureGroup } from '@heroui/react'
import type { ReactNode } from 'react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'

const POSTER_DISPLAY_URL = 'https://media.choice.graphics/product-docs/avery-prismapro/color-swatch-poster-display.webp'
const POSTER_FULL_URL = 'https://media.choice.graphics/product-docs/avery-prismapro/color-swatch-poster-full.webp'
const POSTER_ALT = 'Avery Dennison PrismaPro colour swatch poster'

// ---------------------------------------------------------------------
// Presentational helpers (same shape as avery-sw900.tsx)
// ---------------------------------------------------------------------

function KeyValueTable({ rows }: { rows: Array<{ label: string; value: ReactNode }> }) {
  return (
    <div className='rounded-lg border border-divider overflow-hidden'>
      {rows.map((row, i) => (
        <div
          key={row.label}
          className={`flex flex-col gap-1 px-4 py-2.5 text-sm sm:flex-row sm:gap-6 ${i % 2 === 0 ? 'bg-overlay' : ''}`}
        >
          <span className='text-muted font-medium shrink-0 sm:w-2/5'>{row.label}</span>
          <span className='text-foreground'>{row.value}</span>
        </div>
      ))}
    </div>
  )
}

function BulletList({ items }: { items: ReactNode[] }) {
  return (
    <ul className='list-disc space-y-1.5 pl-5 text-foreground/80'>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

function NumberedList({ items }: { items: ReactNode[] }) {
  return (
    <ol className='list-decimal space-y-1.5 pl-5 text-foreground/80'>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ol>
  )
}

interface ColorRow {
  code: string
  color: string
  extra: string
}

function ColorTable({ headers, rows }: { headers: [string, string, string]; rows: ColorRow[] }) {
  return (
    <div className='overflow-x-auto rounded-lg border border-divider'>
      <table className='w-full min-w-[520px] border-collapse text-sm'>
        <thead>
          <tr className='border-b border-divider text-left'>
            {headers.map(h => (
              <th key={h} className='px-3 py-2 font-medium text-muted'>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.code} className={i % 2 === 0 ? 'bg-overlay' : ''}>
              <td className='px-3 py-2 whitespace-nowrap text-foreground'>{row.code}</td>
              <td className='px-3 py-2 text-foreground'>{row.color}</td>
              <td className='px-3 py-2 text-foreground'>{row.extra}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface DocSectionProps {
  id: string
  title: string
  children: ReactNode
}

function DocSection({ id, title, children }: DocSectionProps) {
  return (
    <Disclosure id={id} aria-label={title} className='overflow-hidden rounded-lg border border-divider bg-surface'>
      <Disclosure.Heading>
        {/* transform-none: HeroUI scales a pressed button to 0.97, which on a
            full-width trigger inside an overflow-hidden card leaves a sliver of
            card showing around the pressed fill. cw-ui-heading: the trigger
            sits in the Disclosure's h3, which the theme sets in Anton; the
            title stays in the body face (spec 3.2 component-heading opt-out). */}
        <Button
          slot='trigger'
          variant='ghost'
          className='h-auto w-full justify-between rounded-none px-4 py-3.5 text-left transform-none'
        >
          <span className='cw-ui-heading text-base font-semibold text-foreground'>{title}</span>
          <Disclosure.Indicator className='text-muted' />
        </Button>
      </Disclosure.Heading>
      <Disclosure.Content>
        <Disclosure.Body className='space-y-3 border-t border-divider px-4 py-4 text-sm text-foreground/80'>
          {children}
        </Disclosure.Body>
      </Disclosure.Content>
    </Disclosure>
  )
}

// ---------------------------------------------------------------------
// Available colours (product overview PDF, page 2)
// ---------------------------------------------------------------------

const AVAILABLE_COLOR_ROWS: ColorRow[] = [
  { code: 'PrismaPro-191-O', color: 'Obsidian Black', extra: 'Gloss' },
  { code: 'PrismaPro-821-O', color: 'Rock Grey', extra: 'Gloss' },
  { code: 'PrismaPro-101-O', color: 'White', extra: 'Gloss' },
  { code: 'PrismaPro-732-O', color: 'Olive Green', extra: 'Gloss' },
  { code: 'PrismaPro-681-O', color: 'Dark Blue', extra: 'Gloss' },
  { code: 'PrismaPro-797-M', color: 'Midnight Sand', extra: 'Gloss Metallic' },
  { code: 'PrismaPro-803-M', color: 'Silver', extra: 'Gloss Metallic' },
  { code: 'PrismaPro-472-M', color: 'Garnet Red', extra: 'Gloss Metallic' },
  { code: 'PrismaPro-566-M', color: 'Purple', extra: 'Gloss Metallic' },
  { code: 'PrismaPro-215-M', color: 'Gold', extra: 'Gloss Metallic' }
]

// ---------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------

export function AveryPrismaproDocs() {
  const [isPosterOpen, setIsPosterOpen] = useState(false)

  return (
    <div className='space-y-8'>
      {/* Poster: visible by default, not collapsed */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Colour Swatch Poster</h3>
        <button
          type='button'
          onClick={() => setIsPosterOpen(true)}
          className='group relative block w-full max-w-sm cursor-zoom-in overflow-hidden rounded-lg border border-divider'
          aria-haspopup='dialog'
          aria-label='Expand the PrismaPro colour swatch poster'
        >
          <img
            src={POSTER_DISPLAY_URL}
            alt={POSTER_ALT}
            loading='lazy'
            decoding='async'
            className='w-full h-auto object-cover transition-transform group-hover:scale-[1.02]'
          />
          <span className='absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white'>
            <Icon icon='fullscreen' className='w-4 h-4' />
            Expand
          </span>
        </button>
      </div>

      {/* Product Overview */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Product Overview</h3>
        <p className='text-xs text-muted mb-3'>Doc A557445, 12/2025</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='overview-intro' title='PrismaPro Color Paint Protection Film'>
            <p className='font-medium text-foreground'>Color That Transforms. Protection That Lasts.</p>
            <p>
              Avery Dennison&reg; PrismaPro&trade; Color Paint Protection Film (PPF) redefines vehicle
              customization by combining the vibrant, head-turning colors of a premium wrap with the robust,
              long-lasting protection of PPF. Designed for precision and engineered with five essential layers,
              PrismaPro is the ultimate upgrade for installers and car owners alike.
            </p>
            <p>
              This is more than just a film; it&rsquo;s an evolution in vehicle customization. With its
              self-healing top-coat, vibrant colors, and installer-friendly, dry-apply technology, PrismaPro
              delivers unmatched performance and protection. Backed by a trusted 10-year warranty, it&rsquo;s the
              ultimate choice for those who demand the best.
            </p>
          </DocSection>

          <DocSection id='features-benefits' title='Features and Benefits'>
            <BulletList
              items={[
                'Dry-Apply Application: Simplifies installation and enables faster turnaround using familiar techniques.',
                'Precision Control: IntelliTack™ Adhesive Technology ensures optimized tack and flawless conformability.',
                'Uncompromising Defense: 8.5 mil TPU construction defends against rock chips, road debris, and everyday wear and tear.',
                'Self-Healing Technology: Our advanced top-coat self-heals with heat, keeping your finish flawless.',
                'Unrivaled Durability: Long-term peace of mind backed by our comprehensive 10-year warranty*.'
              ]}
            />
            <p className='text-xs text-muted'>*Zone 1 Vertical</p>
          </DocSection>

          <DocSection id='product-configuration' title='Product Configuration'>
            <BulletList
              items={[
                'Roll Size: 60” x 75’',
                'Installer-Focused: Optimized for seamless full car wraps, with extra material to ensure you’re always prepared for the unexpected.'
              ]}
            />
          </DocSection>

          <DocSection id='available-colors' title='Available Colors'>
            <p>
              PrismaPro&trade; offers a stunning collection of 10 vibrant, high-impact colors, each designed to
              elevate your vehicle&rsquo;s aesthetic while delivering premium protection.
            </p>
            <ColorTable headers={['Code', 'Color', 'Finish']} rows={AVAILABLE_COLOR_ROWS} />
          </DocSection>
        </DisclosureGroup>
      </div>

      {/* Product Data Sheet */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Product Data Sheet</h3>
        <p className='text-xs text-muted mb-3'>Revision 0, dated 05/15/26</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='pds-uses' title='Uses'>
            <p>
              Avery Dennison&reg; PrismaPro&trade; is an advanced color change Paint Protection Film (PPF)
              designed for experienced installers who demand precision, efficiency, and a flawless, paint-like
              finish. Combining the stunning aesthetics of a premium wrap with the robust durability of PPF,
              PrismaPro&trade; offers exceptional protection and transformative style. The 8.5 mil construction
              is engineered with cutting-edge thermoplastic polyurethane (TPU) and adhesive technology, it guards
              against everyday hazards while delivering striking full-vehicle transformations, special effects,
              or protective accents.
            </p>
            <KeyValueTable
              rows={[
                { label: 'Face Film', value: '7.5 mil (191 µm) gloss top coated polyurethane film' },
                { label: 'Adhesive', value: 'Permanent Acrylic (clear)' },
                { label: 'Liner', value: '4.5 mil Polyester' },
                { label: 'Durability', value: 'Up to 10 years' },
                { label: 'Shelf Life', value: '1 year' },
                { label: 'Application Surfaces', value: 'Flat, simple curves, and compound curves' }
              ]}
            />
          </DocSection>

          <DocSection id='pds-features' title='Features'>
            <BulletList
              items={[
                'IntelliTack™ adhesive technology',
                'Stunning, deep-gloss, paint-like finish',
                'Exceptional 3D conformability with cold stretch capability',
                'Robust self-healing performance',
                'Stain and chip resistance',
                'Long-term durability',
                'Dry-apply design with excellent adhesion',
                'Protective PET cap sheet on gloss for surface uniformity during transport*'
              ]}
            />
          </DocSection>

          <DocSection id='pds-conversion' title='Conversion'>
            <BulletList items={['Flatbed Sign-Cut*', 'Sign-Cut*', 'Hand Cutting*']} />
            <p className='text-xs text-muted'>*Must remove cap sheet</p>
          </DocSection>

          <DocSection id='pds-common-applications' title='Common Applications'>
            <BulletList items={['Automotive', 'Recreational Vehicles']} />
          </DocSection>

          <DocSection id='pds-physical-characteristics' title='Physical Characteristics'>
            <p className='text-xs text-muted'>
              Data represents average values where applicable, and is not intended for specification purposes.
            </p>
            <KeyValueTable
              rows={[
                { label: 'Caliper, face', value: '7.5 mil (191 µm)' },
                { label: 'Caliper, adhesive', value: '1 mil (25 µm)' },
                { label: 'Dimensional stability', value: '<0.0059" (0.15 mm)' },
                { label: 'Tensile at Break', value: 'Opaque: 20-25 lbs/in. Metallic: 12-15 lbs/in.' },
                { label: 'Elongation', value: '>225%' },
                { label: 'Gloss', value: 'Hunter Gloss @ 60 degrees: Gloss - >90 GU' },
                { label: 'Adhesion', value: 'Stainless: 15 min 2.5 lbs/in (438 N/m); 24 hr 3.0 lbs/in (525 N/m)' },
                { label: 'Flammability', value: 'FMVSS 302 Self Extinguishing' },
                { label: 'Shelf-Life', value: '1 year from the date of manufacture (when stored at the following temperatures and humidity conditions 68°-77° F (20° - 25° C) and 50±5% R. H.)' },
                {
                  label: 'Durability',
                  value: (
                    <div className='space-y-1.5'>
                      <div className='grid grid-cols-4 gap-x-3 text-xs'>
                        <span />
                        <span className='font-medium text-foreground'>Zone 1</span>
                        <span className='font-medium text-foreground'>Zone 2</span>
                        <span className='font-medium text-foreground'>Zone 3</span>
                        <span className='font-medium text-foreground'>Vertical</span>
                        <span>10 years</span>
                        <span>8 years</span>
                        <span>6 years</span>
                        <span className='font-medium text-foreground'>Horizontal</span>
                        <span>8 years</span>
                        <span>6 years</span>
                        <span>5 years</span>
                      </div>
                      <p className='text-xs text-muted'>Reference Instructional Bulletin #1.30 for zone map.</p>
                    </div>
                  )
                },
                { label: 'Min. Application Temperature', value: '60° F (16° C) Complex Curves' },
                { label: 'Service Temperature', value: '-40° to 212°F (-40° to 100° C) (Reasonable range of temperatures which would be expected under normal environmental conditions).' },
                { label: 'Rock Chip Testing', value: 'SAE J400 Pass' },
                {
                  label: 'Chemical resistance',
                  value: (
                    <>
                      24 hours, no significant change (after surface wiping with 90% IPA):
                      <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                        <li>Motor Oil</li>
                        <li>Windshield Washer Fluid</li>
                        <li>Water 20% Iron Oxide Rust</li>
                        <li>Road Tar</li>
                        <li>Black Spray Paint</li>
                      </ul>
                    </>
                  )
                },
                { label: 'Compliance', value: 'California Proposition 65' },
                { label: 'MSDS', value: 'Article Letter' }
              ]}
            />
          </DocSection>

          <DocSection id='pds-related-documents' title='Related Documents'>
            <p>
              The following Avery Dennison literature will provide necessary information to the user for proper
              application, storage, and other requirements and is available upon request from your Avery
              Dennison representative or from the Avery Dennison website (www.graphics.averydennison.com).
            </p>
            <KeyValueTable
              rows={[
                { label: 'Substrate Cleaning and Preparation', value: 'Instructional Bulletin #1.10' },
                { label: 'Warranty of Films', value: 'Instructional Bulletin #1.30' },
                { label: 'Cleaning and Maintenance of Vehicle Wraps', value: 'Instructional Bulletin #1.50' },
                { label: 'Paint Protection Film Application', value: 'Instructional Bulletin #4.30' },
                { label: 'Application Instructions for PrismaPro™ Color Paint Protection Film', value: 'Instructional Bulletin #4.40' }
              ]}
            />
          </DocSection>

          <DocSection id='pds-application' title='Application'>
            <p>
              PrismaPro Color Paint Protection Film is designed for application on solid surfaces such as
              passenger vehicles and recreational vehicles.
            </p>
            <p>
              If using PrismaPro Color Paint Protection Film on applications other than passenger vehicles or
              recreational vehicles it is up to the installer to test and determine suitability for use.
            </p>
            <p>
              Avery Dennison PrismaPro Color Paint Protection Film is designed for application on to solid
              surfaces such as passenger vehicles and buses.
            </p>
            <p className='font-medium text-foreground'>Important Note: Never use a wet application method to apply this film.</p>
            <p>
              Some slight color shift may occur between rolls or lots, therefore, minimizing the mixing of
              rolls/lots on a specific vehicle is encouraged. If multiple rolls are needed to be sure to use only
              rolls from the same production lot.
            </p>
            <p>
              All vehicle substrates must be cleaned according to Avery Dennison recommended cleaning practices
              as outlined in Instructional Bulletin #1.10 &ldquo;Substrate Cleaning and Preparation&rdquo;.
            </p>
            <p>
              Due to the unique finish and manufacturing process of metallic and pearlescent films, variances in
              color can be visible when the material is oriented in different directions. When utilizing metallic,
              pearlescent and Color Flow films, always keep the application direction of the film consistent with
              the orientation of the film when cut from the roll and applied to the vehicle. One roll should be
              used per vehicle, wherever possible to minimize any possible color shifts between rolls or lots.
            </p>
            <p>
              See Instructional Bulletin #4.40 &ldquo;Dry Apply PPF Guidelines&rdquo; for complete application
              recommendations. For cleaning and care recommendations see Instructional Bulletin #1.50.
            </p>
            <p>
              NOTE: PrismaPro Series is designed as a wrap film. Cutting for the purpose of preparing sizes or
              shapes has proven to be feasible. However, sign cutting of letters and graphics is not recommended.
            </p>
            <p>
              Important Note: Only use Avery Dennison recommended cleaning products and solutions (noted in
              Instructional Bulletin #1.50). Cleaning and maintenance products should conform to the following:
            </p>
            <BulletList
              items={[
                'Free from abrasive components',
                'Ideally pH balanced, thus not highly acidic or alkaline (pH level between 5 and 9)',
                'Free from alcohol (non isopropyl), acids, ammonia, chlorine, glycol ethers, harmful detergents, petroleum distillates and phosphates'
              ]}
            />
          </DocSection>

          <DocSection id='pds-unsuitable-uses' title='Unsuitable Uses'>
            <p>
              This Avery Dennison product is not designed or recommended for the following uses. Unsuitable
              applications or exposure conditions include, but is not limited to:
            </p>
            <BulletList
              items={[
                'Paint that is not thoroughly cured or dried',
                'Low surface energy substrates (i.e. Tedlar® coatings, rubber, unpainted plastic etc)',
                'Substrate surfaces that are not clean and smooth (little or no variation in texture)',
                'Painted substrates with poor paint-to-substrate, or paint-to-paint bond',
                'Stainless steel',
                'Film applied to non-Avery Dennison Films',
                'Film applied to pre-existing graphics (pre-approval required)',
                'Watercraft below the static water line',
                'Watercraft which are not edge sealed',
                'Non-OEM painted vehicles',
                'Graphic removal from paint with poor adhesion or existing graphics',
                'Graphics exposed to oil, harsh chemicals, or gasoline vapors or spills',
                'Graphics where application tape must adhere to liner',
                'Sign cutting of letters and graphics'
              ]}
            />
          </DocSection>

          <DocSection id='pds-removability' title='Removability'>
            <p>
              Within the limit of its expected lifetime and if applied on properly cured car paint, Avery
              Dennison Paint Protection Film can be removed with nominal adhesive residue. Clean removability is
              not warranted, and removability performance will be compromised when film is applied on existing
              graphics, non OEM paint or other type of surface rather than car paint.
            </p>
            <p>Avery Dennison does not warrant removability from the following substrates:</p>
            <BulletList
              items={[
                'Surfaces with poor paint-to-substrate adhesion',
                'Wallboard (painted or unpainted)',
                'Pre-existing graphics that must remain intact; damage to existing graphic when film is removed',
                'Improperly cured paint',
                'Oxidized or chalked substrates',
                'Stainless Steel'
              ]}
            />
            <p>Avery Dennison makes no warranty for:</p>
            <BulletList
              items={[
                'Paint/Clear Coat Staining: Avery Dennison does not warrant vehicle paint staining that may be visible after removing material which has cracked or discolored. At the first sign of a change in the surface of the material contact your Avery Dennison Sales or Technical Representative.',
                'Ease or speed of removal of any graphic',
                'Removal from automotive paint that is greater than 5 years old',
                'Removal from paint that is improperly cured',
                'Removal from aged paint or metals, surface oxidation or chalking; user must test, approve, and accept liability for such applications',
                'Saltwater marine applications',
                'Marine applications below the water line'
              ]}
            />
          </DocSection>

          <DocSection id='pds-durability' title='Durability'>
            <p>
              Durability is the period of time the product is expected to perform satisfactorily when applied
              vertically or horizontally in any zone or region., provided that the film is properly stored,
              converted and installed in accordance with Avery Dennison guidelines. The term of Avery
              Dennison&rsquo;s warranty is equal to the product&rsquo;s durability.
            </p>
            <p>
              Abrasion and Loss of Gloss are not covered by any Avery Dennison Warranty. These changes are
              considered normal wear and tear.
            </p>
            <p>
              Other factors which may reduce the durability of this product series include: surface texture,
              surface preparation, application method, angle and direction of the sun, environmental conditions,
              cleaning &amp; maintenance.
            </p>
            <p>
              Products containing petroleum distillates or solvents such as soaps, waxes, or protective coatings,
              void any warranty, and may cause premature failure of the material.
            </p>
            <p className='font-medium text-foreground'>
              Failure to install the film in full compliance with Avery Dennison&rsquo;s installation instructions
              may result in personal injury or property damage. Read and follow all instructions when installing
              the film.
            </p>
          </DocSection>
        </DisclosureGroup>
      </div>

      {/* Application Guidelines (IB440) */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Application Guidelines (IB440)</h3>
        <p className='text-xs text-muted mb-3'>Instructional Bulletin #4.40, Revision 0, dated 05/15/26</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='ib440-overview' title='Overview'>
            <p>
              PrismaPro&trade; color Paint Protection Film (PPF) material is a next generation color paint
              protection film engineered for professional dry application. This instructional bulletin provides
              information and recommendations for the successful installation of this innovative product. It
              combines vibrant color and robust paint protection in a seamless, efficient dry-apply process.
            </p>
            <BulletList
              items={[
                'Important Note: As with all graphics films, avoid using hard solvents directly on the film’s surface during or after application.',
                'Before applying the product, consult the appropriate product data sheet for information regarding appropriate substrates and product performance. Once assured that all factors are understood with respect to the product, and all factors comply with the product specifications, you can apply the PrismaPro Paint Protection Films.'
              ]}
            />
          </DocSection>

          <DocSection id='ib440-storage' title='Storage Conditions'>
            <p>
              Avery Dennison&reg; PrismaPro Paint Protection Film should be stored at a Temperature of 70&deg;F
              (±2&deg;F) with 50% RH (± 5%), in the original package and in a vertical position.
            </p>
            <p>
              Once the roll has been opened and if storing a partial of film is necessary, make sure to tape down
              the leftover roll tail with sufficient tension to avoid tunneling. Ensure the material is tightly
              wound around the core and the leading edge is taped down to prevent adhesive imperfections.
              Improper storage can lead to a loss of performance and difficult application. It is recommended to
              use four tabs of rigid PET tape on top, bottom, and middle of the roll and wind tightly prior to
              applying the tape. Alternatively, the cap sheet may be stripped from the entire roll of film. The
              roll should still be rewound with sufficient tension and stored properly.
            </p>
            <p>
              Proper storage allows the film to keep its maximum performance and also enables a hassle-free
              application. The user assumes all risk and liability for the loss of performance, when the film is
              stored improperly by the user. Improper shall mean that the user did not store the Avery Dennison
              Paint Protection Film in accordance with the aforementioned instructions.
            </p>
          </DocSection>

          <DocSection id='ib440-tools' title='Application Tools'>
            <BulletList
              items={[
                'Tool belt: to hold all application tools',
                'Lint-free microfiber cleaning cloth',
                'Clay bar with recommended lubricant',
                'Spray Bottle',
                'Isopropyl Alcohol (IPA) for cleaning',
                'Squeegee with ultra slick buffer (Wet Edge squeegee, Banana Buffer or similar)',
                'Flextreme micro squeegee',
                <>
                  Cutting Tools
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>Knife with stainless steel break off blades</li>
                    <li>Cut tape for PPF film</li>
                  </ul>
                </>,
                'Ultra slick wrap glove (WrapGlove® GHOST® glove or similar type)',
                'Professional Heat Gun: for relaxing the film and locking the edges (DO NOT USE A TORCH)',
                'Tack reducing product, optional (silicone free)'
              ]}
            />
          </DocSection>

          <DocSection id='ib440-prep' title='Preparation and Cleaning of the Surface'>
            <p>
              The vehicle surface must be OEM paint in good condition and cleaned thoroughly to remove all dirt,
              grime, grease, oil, wax, polish, and other contaminants. A clean, smooth, and dry surface is
              critical for proper adhesion.
            </p>
            <p className='font-medium text-foreground'>Cleaning</p>
            <BulletList
              items={[
                <>
                  Do a pre-cleaning wash to remove all dirt and grime. For this step use a water and detergent
                  solution.
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>Special attention should be given to critical areas such as edges, corners, welding seams, rivets and the like.</li>
                    <li>Depending upon the level of contamination, the cleaning process may need to be repeated to ensure that the surface is fully prepared for application.</li>
                    <li>Many other commercially available cleaning/degreasing products may be suitable for cleaning and degreasing but need to be tested prior to use</li>
                  </ul>
                </>,
                'Car wax, polish and ceramic coating residues must be completely removed.',
                'Allow the vehicle to fully dry, using a compressed air gun to remove any trapped water or dry with a lint free cloth',
                'Do not use any polishes, waxes, or tire shining agents post-wash'
              ]}
            />
            <p className='font-medium text-foreground'>Clay Surface</p>
            <BulletList
              items={[
                'Use a professional clay bar or eraser pad with a recommended lubricant to remove any hard-bonded contaminants.',
                'The clay bar will help remove contaminants on the clear coat to ensure the surface is completely smooth.'
              ]}
            />
            <p className='font-medium text-foreground'>Final Surface Cleaning</p>
            <BulletList
              items={[
                'The final cleaning on the vehicle should always be with 70% Isopropyl Alcohol',
                'Pay special attention to recesses, corners, and panel returns.',
                'Allow the vehicle to fully dry, using a compressed air gun to remove any trapped water'
              ]}
            />
            <p>
              For extra information on preparation and cleaning of application surface, additional instructions
              are provided in Instructional Bulletin 1.10
            </p>
            <p className='font-medium text-foreground'>Important Note</p>
            <BulletList
              items={[
                'PrismaPro™ Color PPF is intended and warranted for use on fully cured OEM (Original Equipment Manufacturer) vehicle painted surfaces. Paint should be in “like new condition”. Defects in the paint such as scratches, dents, and orange peel may be visible once the film has been applied.',
                'If heavy orange peel is present on the vehicle it is recommended to perform a professional paint correction prior to installing the PrismaPro™ Color PPF.',
                <>
                  If the vehicle does have non OEM paint, removal of Avery Dennison Paint Protection Film without
                  paint damage is not warranted.
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>If a non OEM paint system is used, all painted surfaces must be fully dry, cured, hardened free of scratches, defects or contamination prior to film application. For most automotive refinishing systems utilizing a bake process through the flash and cure phases, films may typically be applied after the paint has fully cured and has returned to ambient temperature. For air dried or repair paint systems, always consult the paint manufacturer&rsquo;s recommended cure schedule before application. Residual solvents trapped within the paint system may negatively affect adhesion and can result in excessive shrinkage, bubbling or blistering.</li>
                    <li>Painted substrates should be dried according to the paint manufacturer&rsquo;s instructions to avoid solvent retention. Paint system components which are not compatible or do not adhere properly to each other may cause paint to be lifted when films have to be removed after use.</li>
                  </ul>
                </>
              ]}
            />
          </DocSection>

          <DocSection id='ib440-temperature' title='Temperature'>
            <p>
              Temperature plays an important role in how well a film adheres to a substrate. Follow the
              guidelines toward minimum and maximum application temperatures and required service conditions
              before and after application. This information can be found in the Product Data Bulletins for each
              film being used.
            </p>
            <BulletList
              items={[
                'To avoid contamination in the wrap it is important to do the application indoors in a controlled environment. This helps control the temperature and will also reduce the amount of wind, dust and other contaminants.',
                'It is important to monitor both the ambient and surface temperature as both can have an affect on the application. Higher temperatures will make the film soft and more pliable. The high temperature also makes the adhesive more aggressive, which can lead to pre-tack and increased stretching if it is necessary to reposition the film. Lower temperatures will make the film more rigid and reduce the tack of the adhesive.',
                'Ambient Air Temperature: Air temperature of environment',
                'Surface Temperature: Substrate temperature of vehicle or surface'
              ]}
            />
          </DocSection>

          <DocSection id='ib440-installation' title='Installation of the Film'>
            <p>
              Avery Dennison PrismaPro&trade; Color PPF is designed for dry application only. Do not use any slip
              solution, mounting fluids, or soap/water on the adhesive side of the film during this process. The
              Avery Dennison IntelliTack&trade; Technology provides a responsive tack for professional, precise
              placement.
            </p>
            <p className='font-medium text-foreground'>6.1 Positioning</p>
            <NumberedList
              items={[
                'Before starting the application, wash your hands, to avoid fingerprints on the adhesive and ensure that the Avery Dennison Paint Protection Film is applied in a clean, dust-free and well lit environment.',
                'Film panels will be installed and trimmed one panel at a time to ensure full coverage. Measure and cut the film to size from the roll, ensuring it’s 2-3" larger than the panel on each edge. For large flat areas the cap sheet can be left on, however for areas where the film needs to conform to the vehicle surface it will be necessary to remove the cap sheet.',
                'Position the film on the panel using tape. Magnets can leave imperfections in the surface of the film, if they are used be sure they are placed in the bleed areas of the panel.',
                'Carefully separate the liner from the film. (Avoid touching the adhesive surface as much as possible.) For large panels it is not recommended to completely remove the liner from the panel. Remove a portion of the liner to begin installing the first section of the film..'
              ]}
            />
            <p className='font-medium text-foreground'>6.2 Application</p>
            <NumberedList
              items={[
                <>
                  Once the material has been positioned, gently start to squeegee the film. Work from the center
                  of the panel to the outside edges. Overlap the squeegee strokes by about 50% to avoid remaining
                  air entrapment.
                  <p className='mt-1.5'>
                    It is recommended to use the &ldquo;wet face&rdquo; application method to help lubricate the
                    squeegee so it glides smoothly on the film surface. A solution made from baby shampoo and water
                    works well. Take care not to get the solution on the adhesive side of the film.
                  </p>
                  <p className='mt-1.5 text-xs text-muted'>
                    NOTE: Re-positioning of the film is only possible in the stage of positioning and low squeegee
                    pressure. If done in a later stage small marks or lines might remain visible in the film
                    surfaces. When rerepositioning pop the section of the panel using a quick upward and away
                    force.
                  </p>
                </>,
                <>
                  For curved or contoured areas, apply steady, controlled tension to allow the film to naturally
                  conform to the surface. Identify and establish anchor points, locations where the adhesive can
                  be activated with pressure to hold alignment then continue working across the panel. Whenever
                  possible, rely on the film&rsquo;s intrinsic elasticity rather than heat to achieve conformity.
                  If additional pliability is needed, use hands to assist in forming, or a low-temperature heat
                  gun (below 130&deg;F / 55&deg;C) to gently relax the film before applying pressure. Always
                  stretch gradually and evenly;
                  <p className='mt-1.5 text-xs text-muted'>
                    NOTE: With dry-apply PPF, adhesive activation occurs through mechanical pressure. Use soft to
                    medium overlapping squeegee strokes to lock the film in place once alignment and tension are
                    correct.
                  </p>
                </>,
                <>
                  This film is designed to be stretched and conformed at room temperature, using heat will
                  naturally start the curing process, which will increase the initial tack of the adhesive. This
                  can cause glue lines. Additionally, heat plus pressure can cause the air egress pattern to show
                  through.
                  <p className='mt-1.5 text-xs text-muted'>
                    NOTE: Overheating the film in the wrong areas can cause pattern show through and also burn the
                    topcoat, it is essential you follow the proper techniques to get a flawless finish.
                  </p>
                </>,
                'Conventional convex and compound shapes on car fronts are mostly suitable to be covered with Avery Dennison Paint Protection Films, but in some deeper corrugated shapes it may be necessary to make use of inlays in order to avoid over stretching the film.'
              ]}
            />
            <p className='font-medium text-foreground'>6.2.1 Seams and Overlaps</p>
            <p>
              To ensure optimal adhesion and durability, maintain a seam/inlay overlap of 0.25&quot; to 0.5&quot;.
              Strategic placement along the vehicle&rsquo;s natural body lines is highly encouraged to minimize
              visual impact and produce a factory-integrated appearance.
            </p>
            <p className='font-medium text-foreground'>6.2.2 Tack Reducer</p>
            <p>
              The PrismaPro film has an initial tack that is higher than traditional wrap films. Some installers
              may find this challenging and require a tack reducer to help with the installation. GTECHNIQ Quick
              Detailer V2 has been tested and approved by Avery Dennison to be used as a tack reducer. This
              product is recommended on horizontal surfaces to help reduce the initial tack of the PrismaPro
              adhesive during installation. The solution is sprayed onto the surface of the vehicle then wiped off
              and dried using a lint free towel. It is important to note that the tack reducer should be kept away
              from the edges of the panel as well as away from recessed areas as this could cause adhesion issues.
            </p>
            <p className='text-xs text-muted'>
              NOTE: Products containing siloxanes MUST be avoided as they have proven to cause adhesion issues
              with the film.
            </p>
            <p>Alternatively if the GTECHNIQ is not available, a DIY tack reducer can be used. The recommended mix is:</p>
            <BulletList
              items={[
                '32 oz Distilled water',
                '4 ml 99% IPA (or 5.7 ml of 70% IPA)',
                '0.5 ml of J&amp;J Baby Shampoo Soap Solution'
              ]}
            />
            <p>
              Like the GTECHNIQ, this solution would be applied to the surface of the vehicle and then wiped off
              and dried with a lint free towel.
            </p>
            <p className='font-medium text-foreground'>6.3 Finishing Edges and Trimming</p>
            <p>
              Once the film has been applied and effectively squeegeed on the selected application areas, proceed
              to the finishing step by trimming the excess film off the edges.
            </p>
            <p>To facilitate cutting, make sure to use new and very sharp knives for cutting: dull knives will result in rough cut edges.</p>
            <NumberedList
              items={[
                'Use firm pressure with a squeegee to ensure all edges are firmly adhered.',
                'Using a squeegee or glove finish tucking the edge making sure the film is fully applied.',
                'As the final application step, re-squeegee and post heat the most critical areas (e.g. deep corrugation and edges) ensure secure adhesion to the substrate.'
              ]}
            />
            <p className='text-xs text-muted'>
              NOTE: If using a partial roll of film, make sure to tape down the leftover roll tail with sufficient
              tension to avoid tunneling. It is recommended to use 3 tabs of removable tape on top, bottom, and
              middle of the roll and wind tightly prior to applying the tape. Alternatively, the cap sheet can be
              removed from the remainder of the roll and stored without the cap sheet. If this is done take care
              not to wind the roll too tightly as this could cause mottle on the face of the film.
            </p>
          </DocSection>

          <DocSection id='ib440-post-heating' title='Post Heating'>
            <BulletList
              items={[
                'Once application has been completed, all areas where the film has been stretched 10% or more require post heating.',
                'Post heating should be done no sooner than 30-45 minutes after application.',
                'With the use of a heat gun on a high setting and a digital IR thermometer apply heat until the conformed area of film reaches a measured 180°-190°F (80°-90°C)',
                'Post heating must be done the same day as the application.'
              ]}
            />
          </DocSection>

          <DocSection id='ib440-after-install' title='After Install Cleaning &amp; Maintenance'>
            <p>
              Avery Dennison Paint Protection Films features a long-term protection of the car body during the
              lifetime of the product. In order to maintain and clean the film, commercially available cleaning
              products may be suitable but need to be tested prior to use on an inconspicuous area of the film.
            </p>
            <p className='font-medium text-foreground'>8.1 Cleaning</p>
            <BulletList
              items={[
                'Wait 72 hours after installation to wash your car.',
                'Remove insects or other stains as soon as possible with soap and water.',
                'Difficult to remove stains can be removed with isopropyl alcohol > 90%. After using these cleaners, be sure to follow up washing with soap and water after using isopropyl.',
                'Spray waxes that are free of petroleum distillates can also be used to seal the film and may be used to clean and protect the surface of the film.',
                'Light scratches in the film will self-heal in 20-30 minutes at room temperature. In colder conditions, or to accelerate the process, pour warm water (120°F) over the affected area.',
                'Avoid using a pressure washer or high-pressure water source near the edges of the film, it can cause the film to lift and/or become damaged.',
                'Surface contaminants may also be removed using a detailing clay bar or towel.'
              ]}
            />
          </DocSection>

          <DocSection id='ib440-removal' title='Removal'>
            <p>
              Within the limit of its expected lifetime and if applied on properly cured car paint, Avery
              Dennison Paint Protection Film can be removed with nominal adhesive residue. Clean removability is
              not warranted and removability performance will be compromised when film is applied on existing
              graphics, non OEM paint or other type of surface rather than car paint.
            </p>
            <BulletList
              items={[
                'When removing the film, start by picking up a corner and stretching the film somewhere between a 0° and 45° angle.',
                'As the film is being stretched it will start to pop off of the surface and should be removed cleanly. As the angle of removal is increased beyond 45° the occurrence of leaving adhesive residue will also tend to increase. It may be difficult to remove film that has been applied for a long time, or if the film and vehicle are cold.',
                'For the best results, try removing when the temperature of the film and surface are above 55°F. If removed in colder temperatures, a heat gun or hair dryer can be used to aid removal.'
              ]}
            />
          </DocSection>

          <DocSection id='ib440-registration' title='Warranty Registration'>
            <p>Register the PPF installation online at THIS LINK or use this QR code to access the warranty page</p>
          </DocSection>

          <DocSection id='ib440-disclaimer' title='Disclaimer'>
            <p>
              This technical bulletin describes a technique. The information contained herein is believed to be
              reliable, but Avery Dennison makes no warranties, express or implied, including but not limited to
              any implied warranty of merchantability or fitness for a particular purpose. To the extent allowed
              by law, Avery Dennison shall not be liable for any loss or damages, whether direct, indirect,
              special, incidental or consequential, in any way related to the technique of making a graphic
              regardless of the legal theory asserted.
            </p>
            <p className='text-xs text-muted'>Avery Dennison&trade; is a registered trademark of Avery Dennison Corporation</p>
            <p className='text-xs text-muted'>Revisions have been italicized.</p>
          </DocSection>

          <DocSection id='ib440-key-differences' title='Working With PrismaPro: Key Differences vs. SW900'>
            <p className='text-xs text-muted'>
              Key Differences Compared to Avery Dennison&reg; Supreme Wrapping Film&trade; (SW900)
            </p>
            <p className='font-medium text-foreground'>Do</p>
            <BulletList
              items={[
                'Use a Tack Reducer: reduces initial grip, increases repositionability.',
                'Wet Buffer Recommended: avoid scratching and dragging by misting the surface.',
                'Wear an Ultra Slick Glove (WrapGlove® GHOST® or similar): reduces friction and improves application flow.',
                'Wrap Around Edges, Don’t Flush Cut: wrap fully for long-term hold.',
                'Heat Sealing All Edges & Post Heating Recesses: locks the film into contours and recesses.',
                'Work in Sections: avoid full-panel tacking by anchoring in manageable areas.'
              ]}
            />
            <p className='font-medium text-foreground'>Don&rsquo;t</p>
            <BulletList
              items={[
                'Don’t Glass the Whole Panel Like SWF: work the material in sections between body lines.',
                'Don’t Apply Excessive Pressure in Isolated Spots: can cause adhesive distortion or show-through.',
                'Don’t Tuck Under Tight Gaskets: avoid puckering by trimming flush.',
                'Don’t Use Magnets After Removing Cap Sheet: may leave pressure marks.'
              ]}
            />
          </DocSection>
        </DisclosureGroup>
      </div>

      {/* Warranty */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Warranty</h3>
        <p className='text-xs text-muted mb-3'>Dated 06/2026</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='warranty' title='Warranty'>
            <p>
              Avery Dennison Corporation or any of its affiliates and subsidiaries (&ldquo;Avery
              Dennison&rdquo;) warrants Avery Dennison PrismaPro&trade;, Neo&trade;, and Supreme Defense&trade;
              Paint Protection Film (the &ldquo;Products&rdquo;), professionally installed in North America, to
              be free from product manufacturing defects such as adhesive failure, bubbling, cracking, peeling,
              delamination and discoloration. This is a limited warranty and extends to the original purchaser
              only, is not transferable and does not include coverage for improper installation (including but
              not limited to installation which is not performed in accordance with the Avery Dennison
              installation guidelines), poor installation quality, and is subject to the exclusions listed below.
              This warranty gives you specific legal rights, which may vary from state to state.
            </p>
            <p className='font-medium text-foreground'>Limited Warranty</p>
            <p>
              10 years for Supreme Defense&trade; Gloss and PrismaPro&trade; Paint Protection Film. 7 years for
              Supreme Defense&trade; Matte, Supreme Defense&trade; Gloss Lite, and Neo&trade; Paint Protection
              Film.*
            </p>
            <p className='font-medium text-foreground'>Obligations</p>
            <p>
              Avery Dennison&rsquo;s obligation under this warranty is limited to replacement of the defective
              Product for the amount of Product determined to be defective under these warranty terms. If Avery
              Dennison, in its sole discretion, determines that replacement of the defective Product is not
              commercially reasonable, it may issue credit to the original purchaser.
            </p>
            <p className='font-medium text-foreground'>Exclusions and Disclaimer</p>
            <p>
              This warranty is void if the Products are subject to improper care and cleaning (including the use
              of abrasive or unapproved cleaning agents), abuse, misuse, or applied on non-recommended
              substrates, are illegally installed, or are used in an illegal manner.
            </p>
            <p>
              This warranty applies only to Avery Dennison PrismaPro&trade;, Neo&trade;, and Supreme
              Defense&trade; Paint Protection Film. Avery Dennison Paint Protection Film is intended for use on
              fully cured OEM (original equipment manufacturer) vehicle painted surfaces only. This warranty is
              automatically void if installed over repainted, aftermarket painted, or vinyl surfaces. Vehicle
              surface repainting is not covered under this warranty irrespective of the reason for removal. Avery
              Dennison assumes no liability for damage to vehicles resulting from improper care. The warranty
              explicitly does not cover Products installed on non-painted surfaces including but not limited to,
              headlights, indicator lights, chrome or carbon fiber trim. Dealers are not authorized to modify or
              extend this limited warranty in any manner. This Product is to be used only in compliance with all
              applicable laws.
            </p>
            <p className='font-medium text-foreground'>Claims</p>
            <p>
              Avery Dennison warranties are valid anywhere in the USA and Canada. If the original purchaser
              experiences Product failure they must first contact the original installer who installed the
              Product. It is the responsibility of the original purchaser to first notify the original installer,
              or Avery Dennison of evident defect promptly upon its discovery, by describing the defect in detail
              and declaring which claims are asserted. The original installer must be permitted sufficient time
              and opportunity to inspect and examine the Products that were claimed to be defective in order to
              evaluate and respond to any warranty claim. All decisions regarding the existence of manufacturing
              defects which are covered by this limited warranty shall be made by Avery Dennison and shall be
              final and binding. If the original installer is not available, the original purchaser can submit a
              request for warranty service by sending an email or regular mail with a copy of your completed
              warranty certificate and your original bill of sale to the Avery Dennison Corporation Warranty
              Service Department (graphics.quality@averydennison.com). Upon receipt, a representative will help
              locate an installer who can provide warranty service.
            </p>
            <p className='text-xs uppercase text-muted'>
              This warranty is in lieu of any other warranties, express or implied, including, but not limited to,
              warranties of merchantability, fitness for any particular purpose and/or non-infringement, whether
              express or implied by law, course of dealing, course of performance, usage of trade or otherwise.
              Avery Dennison specifically disclaims and excludes all other such warranties. In connection with
              Products, the sale of Products or any contract for sale of Products, and in connection with any
              kind of service provided by Avery Dennison to Purchaser (such as technical services, consultations,
              and responses to product inquiries or claims), no claim by Purchaser of any kind, and no liability
              of Avery Dennison of any kind, shall be greater in amount than the purchase price of the Products or
              Services in respect of which damages are claimed. Purchaser&rsquo;s sole and exclusive remedy is
              replacement of Product or crediting of purchase price, in Avery Dennison&rsquo;s sole discretion.
            </p>
            <p className='text-xs uppercase text-muted'>
              Avery Dennison shall have no liability whatsoever for special, incidental, indirect, punitive,
              exemplary or consequential damages (nor for damages for injuries to persons or property, business
              interruption, diminution in value and lost profits, regardless of whether the damages are
              characterized as direct or consequential damages or otherwise) whether arising out of breach of
              contract, tort (including negligence) or otherwise, and regardless of whether such damages were
              foreseeable and whether or not Avery Dennison has been advised of the possibility of such damages,
              and notwithstanding the failure of any agreed or other remedy of its essential purposes.
            </p>
            <p className='text-xs text-muted'>
              *Neo&trade; PPF, Supreme Defense&trade; Gloss Lite and Supreme Defense&trade; Matte PPF Durability:
              2 year reduction in durability in these states: New Mexico, Nevada, Arizona, Colorado, Southern
              California, and Hawaii.
            </p>
          </DocSection>
        </DisclosureGroup>
      </div>

      <Modal
        isOpen={isPosterOpen}
        onClose={() => setIsPosterOpen(false)}
        title='Colour Swatch Poster'
        size='full'
        backdrop='blur'
        scrollBehavior='inside'
        closeButton
      >
        <img
          src={POSTER_FULL_URL}
          alt={POSTER_ALT}
          loading='lazy'
          decoding='async'
          className='mx-auto max-h-[85vh] w-auto max-w-full object-contain'
        />
      </Modal>
    </div>
  )
}
